import axios from "axios";

// 🔐 Helper: Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("tokenInv");
  return {
    Authorization: `Bearer ${token}`,
    "ngrok-skip-browser-warning": "true",
  };
};

/**
 * Fetch missing document types data for the dashboard drill-down
 * @param {Object} filters - Filter parameters
 * @param {string} filters.start_date - YYYY-MM-DD
 * @param {string} filters.end_date - YYYY-MM-DD
 * @param {number|string} filters.location_id
 * @param {number|string} filters.vendor_id
 * @param {string} filters.country
 */
export const fetchMissingDocTypes = async (filters = {}) => {
  const { start_date, end_date, location_id, vendor_id, country } = filters;

  const params = {
    start_date,
    end_date,
  };

  if (location_id && location_id !== "All") {
    params.location_id = location_id;
  }
  
  if (vendor_id && vendor_id !== -1) {
    params.vendor_id = vendor_id;
  }

  if (country && country !== "All") {
    params.country = country.toLowerCase();
  }

  const response = await axios.get(`${process.env.REACT_APP_URL}/api/dashboard/missing-doc-types`, {
    headers: getAuthHeaders(),
    params,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams(p);
      return searchParams.toString().replace(/\+/g, '%20');
    }
  });

  return response.data;
};

/**
 * Fetch missing contract breakdown data for the dashboard drill-down
 * @param {Object} filters - Filter parameters
 * @param {number|string} filters.vendor_id
 * @param {string} filters.sof_status - SERVICE_NOT_IN_SOF or SERVICE_IN_SOF
 */
export const fetchMissingContractBreakdown = async (filters = {}) => {
  const { vendor_id, sof_status } = filters;

  const params = {
    sof_status
  };

  if (vendor_id && vendor_id !== -1) {
    params.vendor_id = vendor_id;
  }

  const response = await axios.get(`${process.env.REACT_APP_URL}/api/discrepancy/missing-contract-breakdown`, {
    headers: getAuthHeaders(),
    params,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams(p);
      return searchParams.toString().replace(/\+/g, '%20');
    }
  });

  return response.data;
};

/**
 * Fetch discrepancy breakdown data (positive/negative)
 * @param {Object} filters - Filter parameters
 * @param {number|string} filters.vendor_id
 * @param {string} filters.discrepancy_type - positive or negative
 */
export const fetchDiscrepancyBreakdown = async (filters = {}) => {
  const { vendor_id, discrepancy_type } = filters;

  const params = {
    discrepancy_type
  };

  if (vendor_id && vendor_id !== -1) {
    params.vendor_id = vendor_id;
  }

  const response = await axios.get(`${process.env.REACT_APP_URL}/api/discrepancy/breakdown`, {
    headers: getAuthHeaders(),
    params
  });

  return response.data;
};

/**
 * Fetch invoices for missing contract breakdown (click on invoice count)
 * @param {Object} filters - Filter parameters
 * @param {string} filters.doc_id
 * @param {string} filters.service_id
 */
export const fetchMissingContractInvoices = async (filters = {}) => {
  const { doc_id, service_id, sof_status, is_csv } = filters;

  const params = {
    doc_id,
    service_id
  };

  if (sof_status) params.sof_status = sof_status;
  if (is_csv) params.is_csv = true;

  const response = await axios.get(`${process.env.REACT_APP_URL}/api/discrepancy/missing-contract-invoices`, {
    headers: getAuthHeaders(),
    params
  });

  return response.data;
};

/**
 * Fetch removed services data (count and listing)
 * @param {Object} filters - Filter parameters
 * @param {number|string} filters.vendor_id
 * @param {string} filters.start_date
 * @param {string} filters.end_date
 */
export const fetchRemovedServices = async (filters = {}) => {
  const response = await axios.get(`${process.env.REACT_APP_URL}/api/discrepancy/removed-services-comparison`, {
    headers: getAuthHeaders(),
    params: filters,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams(p);
      return searchParams.toString().replace(/\+/g, '%20');
    }
  });

  return response.data;
};

/**
 * Fetch credit achievement details
 * @param {Object} filters
 * @param {string} filters.start_date
 * @param {number|string} filters.vendor_id
 */
export const fetchCreditAchievement = async (filters = {}) => {
  const response = await axios.get(`${process.env.REACT_APP_URL}/api/get_credit_achievement`, {
    headers: getAuthHeaders(),
    params: filters,
  });
  return response.data;
};

/**
 * Fetch circuit report data
 * @param {Object} filters - Filter parameters
 * @param {string} filters.start_date - YYYY-MM-DD format
 * @param {string} filters.end_date - YYYY-MM-DD format
 * @param {boolean} filters.download_csv - false for JSON data, true for CSV download
 */
export const fetchCircuitReport = async (filters = {}) => {
  const { start_date, end_date, download_csv = false } = filters;

  const formData = new FormData();
  if (start_date) formData.append("start_date", start_date);
  if (end_date) formData.append("end_date", end_date);
  formData.append("download_csv", download_csv ? "true" : "false");

  const response = await axios.post(`${process.env.REACT_APP_URL}/api/circuit_report`, formData, {
    headers: {
      ...getAuthHeaders(),
      // Don't set Content-Type, let axios set it automatically with FormData
    },
  });

  return response.data;
};

/**
* Fetch grouped services details (discrepancies at the service/invoice level)
* @param {Object} filters - Filter parameters
*/
export const fetchGroupedServicesDetails = async (filters = {}) => {
  const { start_date, end_date, vendor_id, location_id, search_for, service_id, invoice_id, classification, page, limit } = filters;
 
  const params = {};
  
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;
  if (vendor_id && vendor_id !== -1 && vendor_id !== 'all') params.vendor_id = vendor_id;
  if (location_id && location_id !== 'All') params.location_id = location_id;
  if (search_for) params.search_for = search_for;
  if (service_id) params.service_id = service_id;
  if (invoice_id) params.invoice_id = invoice_id;
  if (classification) params.classification = classification;
  if (page) params.page = page;
  if (limit) params.limit = limit;
 
  const response = await axios.get(`${process.env.REACT_APP_URL}/api/discrepancy/grouped-services-details`, {
    headers: getAuthHeaders(),
    params
  });
 
  return response.data;
};
 
/**
 * Fetch country breakdown data for the dashboard drill-down
 * @param {Object} filters - Filter parameters
 * @param {string} filters.start_date
 * @param {string} filters.end_date
 * @param {number|string} filters.location_id
 * @param {number|string} filters.vendor_id
 * @param {string} filters.country
 */
export const fetchCountryBreakdown = async (filters = {}) => {
  const { start_date, end_date, location_id, vendor_id, country } = filters;

  const params = {
    start_date,
    end_date,
  };

  if (location_id && location_id !== "All") {
    params.location_id = location_id;
  }
  
  if (vendor_id && vendor_id !== -1) {
    params.vendor_id = vendor_id;
  }

  if (country && country !== "All") {
    params.country = country.toLowerCase();
  }

  const response = await axios.get(`${process.env.REACT_APP_URL}/api/dashboard/country-breakdown`, {
    headers: getAuthHeaders(),
    params,
    paramsSerializer: (p) => {
      const searchParams = new URLSearchParams(p);
      return searchParams.toString().replace(/\+/g, '%20');
    }
  });

  return response.data;
};

/**
 * Fetch SOF matched docs for a specific service and invoice discrepancy
 * @param {string} service_id
 * @param {string} invoice_id
 */
export const fetchMatchedSofDocs = async (service_id, invoice_id) => {
  const response = await axios.get(`${process.env.REACT_APP_URL}/api/grouped-discrepancy/services-matched-in-sof-docs`, {
    headers: getAuthHeaders(),
    params: {
      service_id,
      invoice_id,
    }
  });
  return response.data;
};

/**
 * Fetch Idle Circuits Summary
 * @param {Array} thresholds - Array of integers (e.g. [1,2,3,4,5,6])
 */
export const fetchIdleCircuitsSummary = async (thresholds = [1, 2, 3, 4, 5, 6]) => {
  const response = await axios.post(`${process.env.REACT_APP_URL}/api/idle_circuits/summary`, {
    thresholds
  }, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch Idle Circuits Trend
 */
export const fetchIdleCircuitsTrend = async () => {
  const response = await axios.post(`${process.env.REACT_APP_URL}/api/idle_circuits/trend`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch Idle Circuits Detail
 * @param {number} months - Number of months idle
 */
export const fetchIdleCircuitsDetail = async (months) => {
  const response = await axios.post(`${process.env.REACT_APP_URL}/api/idle_circuits/detail`, {
    months
  }, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch Location Activity Summary
 */
export const fetchLocationActivitySummary = async () => {
  const response = await axios.post(`${process.env.REACT_APP_URL}/api/location_activity/summary`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch Location Activity Detail
 * @param {number} months - Threshold in months
 * @param {string} status - 'active' or 'inactive'
 */
export const fetchLocationActivityDetail = async (months, status) => {
  const response = await axios.post(`${process.env.REACT_APP_URL}/api/location_activity/detail`, {
    months,
    status
  }, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch all invoices (same endpoint as Document → Invoice tab)
 * @param {Object} filters
 * @param {number|string} filters.vendor_id
 * @param {string} filters.search_for
 * @param {string} filters.year
 * @param {string} filters.month
 * @param {string} filters.currency
 */
export const fetchAllInvoices = async (filters = {}) => {
  const { vendor_id, search_for, year, month, currency } = filters;

  let url = `${process.env.REACT_APP_URL}/get-all-invoices?search_for=${search_for || ''}`;
  if (vendor_id && vendor_id !== -1) url += `&vendor_id=${vendor_id}`;
  if (year) url += `&year=${year}`;
  if (month && month !== 'all') url += `&month=${month}`;
  if (currency && currency !== 'all') url += `&currency=${currency}`;

  const response = await axios.get(url, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch discrepancy data for a specific invoice
 * @param {string} invoiceId
 */
export const fetchInvoiceDiscrepancy = async (invoiceId) => {
  const response = await axios.get(`${process.env.REACT_APP_URL}/get-discrepancy/${invoiceId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Fetch invoice line-item details by document ID
 * @param {string} docId
 */
export const fetchInvoiceDetailByDoc = async (docId) => {
  const response = await axios.get(`${process.env.REACT_APP_URL}/get-invoice-details-by-doc/${docId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};